// client/src/hooks/useStudents.js
// CRUD hooks for the students nested resource. Hands a `query` object to
// the listing endpoint (className, q, hasCard, limit, offset) — the
// queryKey includes it so different filter combinations cache separately.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, uploadXlsx } from '../lib/api.js';

const listKey = (campId, query) => ['camps', campId, 'students', query];
const detailKey = (campId, studentId) => ['camps', campId, 'students', 'detail', studentId];

export function useStudents(campId, query = {}) {
  return useQuery({
    queryKey: listKey(campId, query),
    queryFn: () => api.get(`/api/camps/${campId}/students`, { query }),
    enabled: !!campId,
    // Keep previous data on filter change so the table doesn't blank out.
    placeholderData: (prev) => prev,
  });
}

/**
 * Fetch the WHOLE roster (no server-side filtering) by paging through the
 * list endpoint until we've collected `total` rows. The back office then
 * filters / searches / paginates client-side, so class chips, KPIs and the
 * free-text search reflect every student — not just the current 50-row page.
 *
 * The server caps `limit` at 500, so we loop in 500-row pages. A camp tops
 * out around the 2000-row import cap, i.e. ≤4 requests.
 */
export function useAllStudents(campId) {
  return useQuery({
    queryKey: ['camps', campId, 'students', 'all'],
    queryFn: async () => {
      const PAGE = 500;
      const all = [];
      let offset = 0;
      let total = Infinity;
      while (offset < total) {
        const res = await api.get(`/api/camps/${campId}/students`, {
          query: { limit: PAGE, offset },
        });
        all.push(...res.students);
        total = res.total;
        offset += PAGE;
        if (res.students.length < PAGE) break; // last page reached
      }
      return { students: all, total: all.length };
    },
    enabled: !!campId,
    placeholderData: (prev) => prev,
  });
}

export function useStudent(campId, studentId) {
  return useQuery({
    queryKey: detailKey(campId, studentId),
    queryFn: () =>
      api.get(`/api/camps/${campId}/students/${studentId}`).then((r) => r.student),
    enabled: !!(campId && studentId),
  });
}

function invalidateLists(qc, campId) {
  qc.invalidateQueries({ queryKey: ['camps', campId, 'students'] });
}

export function useCreateStudent(campId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post(`/api/camps/${campId}/students`, body),
    onSuccess: () => invalidateLists(qc, campId),
  });
}

export function useUpdateStudent(campId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ studentId, patch }) =>
      api.patch(`/api/camps/${campId}/students/${studentId}`, patch),
    onSuccess: () => invalidateLists(qc, campId),
  });
}

export function useDeleteStudent(campId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (studentId) =>
      api.delete(`/api/camps/${campId}/students/${studentId}`),
    onSuccess: () => invalidateLists(qc, campId),
  });
}

/** Archive many students at once. Takes an array of ids. */
export function useBulkDeleteStudents(campId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids) =>
      api.post(`/api/camps/${campId}/students/bulk-delete`, { ids }),
    onSuccess: () => invalidateLists(qc, campId),
  });
}

/**
 * Manual point credit to one or many students. Refreshes the roster plus the
 * transaction history + leaderboard so balances reflect the new credit.
 */
export function useCreditStudents(campId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ studentIds, amount, reason, direction }) =>
      api.post(`/api/camps/${campId}/students/credit`, { studentIds, amount, reason, direction }),
    onSuccess: () => {
      invalidateLists(qc, campId);
      qc.invalidateQueries({ queryKey: ['camps', campId, 'transactions'] });
      qc.invalidateQueries({ queryKey: ['camps', campId, 'leaderboard'] });
      qc.invalidateQueries({ queryKey: ['camps', campId, 'student-balance'] });
    },
  });
}

/**
 * Balances for the whole roster as a { [studentId]: points } map. Fetched
 * separately (and in parallel) from the student list so it never slows the
 * roster render — the list paints immediately and the numbers fill in. Shares
 * the 'student-balance' key prefix, so credit/import invalidations refresh it.
 */
export function useStudentBalances(campId) {
  return useQuery({
    queryKey: ['camps', campId, 'student-balance', 'map'],
    queryFn: () =>
      api.get(`/api/camps/${campId}/students/balances`).then((r) => r.balances),
    enabled: !!campId,
    placeholderData: (prev) => prev,
  });
}

/** A single student's live balance — cheap, so the card can load it eagerly
 *  while the heavier transaction history loads only when expanded. */
export function useStudentBalance(campId, studentId, options = {}) {
  return useQuery({
    queryKey: ['camps', campId, 'student-balance', studentId],
    queryFn: () =>
      api.get(`/api/camps/${campId}/students/${studentId}/balance`).then((r) => r.balance),
    enabled: !!campId && !!studentId && (options.enabled ?? true),
  });
}

/**
 * Multipart XLSX upload. Uses raw fetch (not the api.* wrapper) because we
 * need multipart/form-data, not JSON. Same cookie-credentialed shape.
 */
/**
 * Two-phase XLSX import for the ImportWizard:
 *   analyze(file) — dry run, returns the column report + projected summary
 *   commit(file)  — performs the import + refreshes students/classes caches
 */
export function useStudentImport(campId) {
  const qc = useQueryClient();
  const base = `/api/camps/${campId}/students/import`;
  return {
    analyze: (file) => uploadXlsx(`${base}?dryRun=1`, file),
    commit: async (file) => {
      const r = await uploadXlsx(base, file);
      invalidateLists(qc, campId);
      qc.invalidateQueries({ queryKey: ['camps', campId, 'classes'] });
      return r;
    },
  };
}

/**
 * Two-phase XLSX bulk point-credit (all-or-nothing). analyze(file) previews
 * the match (no writes); commit(file) credits each matched student and
 * refreshes balances / history / leaderboard.
 */
export function usePointsImport(campId) {
  const qc = useQueryClient();
  const base = `/api/camps/${campId}/students/import-points`;
  return {
    analyze: (file) => uploadXlsx(`${base}?dryRun=1`, file),
    commit: async (file) => {
      const r = await uploadXlsx(base, file);
      invalidateLists(qc, campId);
      qc.invalidateQueries({ queryKey: ['camps', campId, 'transactions'] });
      qc.invalidateQueries({ queryKey: ['camps', campId, 'leaderboard'] });
      qc.invalidateQueries({ queryKey: ['camps', campId, 'student-balance'] });
      return r;
    },
  };
}
